import pages from './pages.json';
import { products } from '@/containers/Products/ProductsArray';
import getProductImage from '@/assets/images/products/getProductImages';
import { breadcrumbJsonLd, breadcrumbsFor, businessJsonLd, productJsonLd, serviceJsonLd, type JsonLd } from './jsonLd';
import { clamp, normalizePath } from './url';
import type { SeoInput } from './useSeo';

type PageMeta = { title: string; description: string };
const PAGES = pages as Record<string, PageMeta>;

const PLACEHOLDER_IMAGE = 'imgnotavailable.png';
/** Pages that describe the business itself, so they carry the business structured data. */
const BUSINESS_PAGES = new Set(['/', '/about', '/contact']);

/** "Cementitious Waterproofing in Kathmandu, Nepal | Sheel Waterproofing" -> the part before " | ". */
const shortName = (title: string) => title.split(' | ')[0];

/**
 * Title and description for every page whose content is known at build time: the fixed pages
 * (pages.json) and the product pages (ProductsArray). Pages whose content comes from the CMS
 * (project, and CMS product/service details) set their own with `useSeo` once loaded.
 */
export function getRouteSeo(pathname: string): SeoInput | null {
  const path = normalizePath(pathname);

  const page = PAGES[path];
  if (page) {
    const jsonLd: JsonLd[] = BUSINESS_PAGES.has(path)
      ? [businessJsonLd()]
      : [breadcrumbJsonLd(breadcrumbsFor(path, shortName(page.title)))];

    if (path.startsWith('/services/')) {
      jsonLd.push(serviceJsonLd({ name: shortName(page.title), description: page.description, path }));
    }
    return { ...page, path, jsonLd };
  }

  // "/gallery/all" is fixed and always exists, so it's known here; a real category is a
  // Sanity document (see galleryCategory.ts) fetched at runtime, not known at build time — like
  // project/product/service CMS detail pages, GalleryCategoryPage sets its own tags once its
  // data (including whether the slug is even real) has arrived.
  if (path === '/gallery/all') {
    const title = 'All Photos & Videos | Sheel Waterproofing';
    const description = 'Every waterproofing project photo and video from Sheel Waterproofing, in one place.';
    return { title, description, path, jsonLd: [breadcrumbJsonLd(breadcrumbsFor(path, 'All Photos & Videos'))] };
  }
  if (/^\/gallery\/[^/]+$/.test(path)) {
    return null;
  }

  const product = products.find((candidate) => candidate.linkUrl === path);
  if (product) {
    const title = `${product.cardTitle} | Sheel Waterproofing Nepal`;
    const description = clamp(product.cardContent);
    const image = product.imageFileName === PLACEHOLDER_IMAGE ? undefined : getProductImage(product.imageFileName);

    return {
      title,
      description,
      path,
      image,
      imageAlt: product.cardTitle,
      jsonLd: [
        breadcrumbJsonLd(breadcrumbsFor(path, product.cardTitle)),
        productJsonLd({ name: product.cardTitle, description, path, image }),
      ],
    };
  }

  return null;
}
